/**
 * @file Synchronisation de la sauvegarde avec le serveur : arbitrage au demarrage (local / distant), envoi differe des sauvegardes locales, gestion des conflits.
 * Pur vis-a-vis du reseau (le transport est un SyncAdapter) : testable avec un faux serveur. Les revisions locale et serveur sont des compteurs distincts :
 * on retient la revision serveur vue lors de la derniere synchro pour savoir si l'autre cote a bouge depuis.
 */
import type { RemoteSave } from './SyncAdapter';

/** Stockage cle / valeur minimal (le StorageAdapter de platform/ y satisfait structurellement : net/ n'importe pas platform/). */
export interface KeyValueStore {
  load(key: string): Promise<string | null>;
  save(key: string, value: string): Promise<void>;
}

export const CLOUD_META_KEY = 'catgunner2.cloud';

/** Delai minimal (ms) entre deux envois ; la sauvegarde locale, elle, reste toutes les 10 s. */
export const PUSH_MIN_INTERVAL_MS = 20_000;
/** Apres des echecs consecutifs (serveur injoignable), le delai double a chaque echec, jusqu'a x8 (2 min 40 s). */
const MAX_BACKOFF_STEPS = 3;
/** Ecart (ms) au-dela duquel une version distante est consideree plus recente que la locale. */
const NEWER_MARGIN_MS = 1000;

export interface CloudMeta {
  userId: string;
  /** Revision serveur de la derniere synchro reussie. */
  serverRevision: number;
  /** Reinitialisation / import : la prochaine version locale ecrase la version serveur sans arbitrage. */
  forceNext?: boolean;
}

/** Transport minimal necessaire (ServerSyncAdapter + forcePush). */
export interface CloudTransport {
  readonly revision: number;
  pull(): Promise<RemoteSave | null>;
  push(playerId: string, save: RemoteSave): Promise<{ accepted: boolean; serverRevision: number }>;
  forcePush(save: RemoteSave): Promise<number>;
}

export type BootChoice = 'local' | 'remote';
export interface BootDecision {
  choice: BootChoice;
  remote: RemoteSave | null;
  /** 'adopted' : la version distante remplace la locale ; 'newer-kept' : conflit, la locale (plus recente) est gardee ; 'forced' : locale poussee d'office. */
  reason: 'no-remote' | 'unchanged' | 'new-device' | 'adopted' | 'newer-kept' | 'forced';
}

export type PushOutcome = { kind: 'pushed' } | { kind: 'forced' } | { kind: 'remote-wins'; remote: RemoteSave } | { kind: 'failed'; error: unknown };

/** Extrait revision / date d'une enveloppe de sauvegarde brute (tolerant : valeurs a 0 si illisible). */
export const describeEnvelope = (raw: string): { revision: number; savedAtMs: number } => {
  try {
    const e = JSON.parse(raw) as { revision?: unknown; savedAtMs?: unknown };
    return { revision: typeof e.revision === 'number' ? e.revision : 0, savedAtMs: typeof e.savedAtMs === 'number' ? e.savedAtMs : 0 };
  } catch {
    return { revision: 0, savedAtMs: 0 };
  }
};

export const toRemoteSave = (raw: string): RemoteSave => ({ ...describeEnvelope(raw), payload: raw });

export class CloudSync {
  private meta: CloudMeta;
  private dirty = false;
  private lastPushAt = 0;
  private pushing = false;
  private failures = 0;

  constructor(
    private readonly transport: CloudTransport,
    private readonly storage: KeyValueStore,
    readonly userId: string,
    meta: CloudMeta | null,
  ) {
    // repere d'un autre compte : oublie, sauf une demande d'ecrasement posee hors ligne (userId vide)
    if (meta && meta.userId === userId) this.meta = meta;
    else this.meta = { userId, serverRevision: -1, ...(meta && (meta.userId === '' || meta.forceNext) ? { forceNext: meta.forceNext === true } : {}) };
  }

  /** Pose « ecraser le serveur au prochain envoi » sans connexion (reinitialisation hors ligne) : prise en compte a la prochaine connexion. */
  static async markForceNextOffline(storage: KeyValueStore): Promise<void> {
    let meta: CloudMeta = { userId: '', serverRevision: -1 };
    try {
      const raw = await storage.load(CLOUD_META_KEY);
      if (raw) meta = JSON.parse(raw) as CloudMeta;
    } catch {
      /* repere illisible : on repart d'un repere vide */
    }
    await storage.save(CLOUD_META_KEY, JSON.stringify({ ...meta, forceNext: true }));
  }

  static async create(transport: CloudTransport, storage: KeyValueStore, userId: string): Promise<CloudSync> {
    let meta: CloudMeta | null;
    try {
      const raw = await storage.load(CLOUD_META_KEY);
      meta = raw ? (JSON.parse(raw) as CloudMeta) : null;
    } catch {
      meta = null;
    }
    return new CloudSync(transport, storage, userId, meta);
  }

  private async persist(): Promise<void> {
    await this.storage.save(CLOUD_META_KEY, JSON.stringify(this.meta));
  }

  /**
   * Arbitrage au demarrage. `local` = sauvegarde locale brute (null si aucune), `localIsNew` = partie vierge creee a l'instant.
   * Regle : partie vierge → distante ; distante inchangee depuis la derniere synchro → locale ; sinon la plus recente (date de sauvegarde) l'emporte.
   */
  async reconcile(local: RemoteSave | null, localIsNew: boolean): Promise<BootDecision> {
    const remote = await this.transport.pull();
    if (this.meta.forceNext) {
      // reinitialisation / import : la version locale gagne d'office. Partie vierge pas encore ecrite : elle partira a la premiere sauvegarde.
      if (local && !localIsNew) {
        this.meta.serverRevision = await this.transport.forcePush(local);
        this.meta.forceNext = false;
        await this.persist();
      }
      return { choice: 'local', remote, reason: 'forced' };
    }
    if (!remote) {
      this.dirty = local !== null;
      return { choice: 'local', remote: null, reason: 'no-remote' };
    }
    if (!local || localIsNew) {
      this.meta.serverRevision = remote.revision;
      await this.persist();
      return { choice: 'remote', remote, reason: 'new-device' };
    }
    if (this.meta.serverRevision === remote.revision) {
      this.dirty = true;
      return { choice: 'local', remote, reason: 'unchanged' };
    }
    if (remote.savedAtMs > local.savedAtMs + NEWER_MARGIN_MS) {
      this.meta.serverRevision = remote.revision;
      await this.persist();
      return { choice: 'remote', remote, reason: 'adopted' };
    }
    // la locale est plus recente : on l'impose au serveur au prochain envoi (l'ancienne version reste dans l'historique du serveur)
    this.meta.forceNext = true;
    this.dirty = true;
    await this.persist();
    return { choice: 'local', remote, reason: 'newer-kept' };
  }

  /** Une sauvegarde locale vient d'etre ecrite. */
  markDirty(): void {
    this.dirty = true;
  }

  get hasPending(): boolean {
    return this.dirty;
  }

  /** Reinitialisation / import : la version locale a venir remplacera celle du serveur. */
  async markForceNext(): Promise<void> {
    this.meta.forceNext = true;
    await this.persist();
  }

  /** Envoie la sauvegarde si elle a change et que le delai minimal est ecoule (`force` : tout de suite, ex. mise en arriere-plan). */
  async pushIfDue(nowMs: number, readLocal: () => Promise<string | null>, force = false): Promise<PushOutcome | null> {
    if (!this.dirty || this.pushing) return null;
    if (!force && nowMs - this.lastPushAt < PUSH_MIN_INTERVAL_MS * 2 ** Math.min(this.failures, MAX_BACKOFF_STEPS)) return null;
    const raw = await readLocal();
    if (!raw) return null;
    this.pushing = true;
    this.lastPushAt = nowMs;
    try {
      const out = await this.push(toRemoteSave(raw));
      this.failures = out.kind === 'failed' ? this.failures + 1 : 0;
      return out;
    } finally {
      this.pushing = false;
    }
  }

  private async push(local: RemoteSave): Promise<PushOutcome> {
    try {
      if (this.meta.forceNext) {
        this.meta.serverRevision = await this.transport.forcePush(local);
        this.meta.forceNext = false;
        this.dirty = false;
        await this.persist();
        return { kind: 'forced' };
      }
      const r = await this.transport.push(this.userId, local);
      if (r.accepted) {
        this.meta.serverRevision = r.serverRevision;
        this.dirty = false;
        await this.persist();
        return { kind: 'pushed' };
      }
      // conflit : un autre appareil a pousse entre-temps
      const remote = await this.transport.pull();
      if (remote && remote.savedAtMs > local.savedAtMs + NEWER_MARGIN_MS) {
        this.meta.serverRevision = remote.revision;
        await this.persist();
        return { kind: 'remote-wins', remote };
      }
      this.meta.serverRevision = await this.transport.forcePush(local);
      this.dirty = false;
      await this.persist();
      return { kind: 'forced' };
    } catch (error) {
      return { kind: 'failed', error };
    }
  }
}
